<script setup lang="ts">
import { computed, ref } from "vue";
import Button from "@/stories/buttons/Button/Button.vue";
import TextInput from "@/stories/form/TextInput/TextInput.vue";
import { BREAKPOINTS, PIXABAY_API_KEY } from "@/consts";
import type { IPixabayImage, IPixabayResult } from "@/interfaces";
import { sentryException } from "@/sentry";
import { simpleError } from "@/stories/displays/SimpleError/SimpleError.vue";
import LoadingDialog from "@/stories/dialogs/LoadingDialog.vue";
import ImageCheckbox from "@/stories/form/ImageCheckbox/ImageCheckbox.vue";
import Message from "@/stories/displays/Message/Message.vue";
import { useBreakpoints } from "@vueuse/core";
import { debounce } from "lodash";
import { useAuthStore } from "@/stores/auth.module";
import BigDialog from "@/stories/dialogs/BigDialog.vue";
import DialogHeader from "@/stories/dialogs/DialogHeader.vue";
import DialogSubHeader from "@/stories/dialogs/DialogSubHeader.vue";
import DialogContent from "@/stories/dialogs/DialogContent.vue";

interface Props {
  max: number;
}

defineProps<Props>();

const emit = defineEmits<{
  close: [];
  select: [IPixabayImage[]];
}>();

const authStore = useAuthStore();
const breakpoints = useBreakpoints(BREAKPOINTS);
const isMobile = breakpoints.smaller("desktop");

const selectedImages = ref<number[]>([]);
const loading = ref<string | null>(null);
const searchText = ref("");
const searchResult = ref<IPixabayResult>();

const images = computed(() => {
  if (!searchResult.value) {
    return [];
  }

  return [...searchResult.value.hits].slice(0, 12);
});

const fetchImages = async (event?: Event) => {
  event?.preventDefault();
  const showLoading = !!event;

  try {
    selectedImages.value = [];

    if (showLoading) {
      loading.value = "templates.pixabay.loading_message";
    }

    const url = new URL("https://pixabay.com/api/");
    url.searchParams.append("key", PIXABAY_API_KEY);
    url.searchParams.append("q", searchText.value);
    url.searchParams.append("lang", authStore.institutionLocale);
    url.searchParams.append("image_type", "photo");
    url.searchParams.append("min_width", "200");
    url.searchParams.append("min_height", "200");
    url.searchParams.append("per_page", "12");
    const response = await fetch(url);
    searchResult.value = await response.json();
  } catch (error) {
    sentryException(error);
    simpleError("global.error_api");
  } finally {
    loading.value = null;
  }
};

const toggleImages = (image: IPixabayImage) => {
  if (selectedImages.value.includes(image.id)) {
    selectedImages.value = selectedImages.value.filter((id) => image.id !== id);
  } else {
    selectedImages.value.push(image.id);
  }
};

const handleApply = () => {
  const selections = (searchResult.value?.hits || []).filter((item) =>
    selectedImages.value.includes(item.id),
  );
  emit("select", selections);
};

const debouncedFetch = debounce(fetchImages, 300);

const handleSearchUpdate = (value: string) => {
  searchText.value = value;
  debouncedFetch();
};
</script>

<template>
  <BigDialog @close="$emit('close')">
    <DialogHeader @close="$emit('close')">
      {{ $t("templates.online_library") }}
    </DialogHeader>

    <DialogSubHeader>
      <form @submit="fetchImages" class="flex justify-between gap-4 w-full">
        <TextInput
          name="search"
          rounded
          size="sm"
          icon="fa fa-search"
          background="bg-neutral100"
          :placeholder="$t('templates.search_image')"
          :helpText="
            !searchText.length && !searchResult
              ? $t('templates.pixabay.search_help_text')
              : undefined
          "
          autoFocus
          :modelValue="searchText"
          @update:modelValue="handleSearchUpdate"
        />

        <Button
          iconOnly
          icon="fa-search w-10 min-w-10"
          type="white"
          :buttonType="'submit'"
          @click="fetchImages"
        />
      </form>
    </DialogSubHeader>

    <DialogContent>
      <div class="flex flex-col gap-4 space-between relative">
        <Message variant="error" v-if="selectedImages.length > max">
          <span class="h300">
            {{ $t("templates.pixabay.error_max_select_new", { max }) }}
          </span>
        </Message>

        <div
          class="grid grid-rows-3 grid-cols-2 gap-4 desktop:mt-0 desktop:grid-cols-4 min-h-[434px]"
        >
          <ImageCheckbox
            v-for="image of images"
            :key="image.id"
            :src="image.previewURL"
            :alt="image.tags"
            :checked="selectedImages.includes(image.id)"
            @toggle="() => toggleImages(image)"
          />
        </div>
      </div>
    </DialogContent>

    <div
      :class="[
        'flex space-between items-center fixed left-0 bottom-0 bg-white w-full p-6 border-t mt-1 border-neutral300',
        'tablet:relative tablet:border-none',
      ]"
    >
      <div v-if="!isMobile" class="flex-grow h600 text-darkOcean600">
        {{
          $t("templates.amount_images_chosen_new", {
            count: selectedImages.length,
          })
        }}
      </div>
      <Button
        v-if="!isMobile"
        :text="$t('templates.cancel')"
        type="white"
        class="mr-2"
        @click="$emit('close')"
      />
      <Button
        type="primary"
        :text="$t('global.use')"
        :disabled="selectedImages.length > max"
        :full-width="isMobile"
        :size="isMobile ? 'lg' : 'md'"
        @click="handleApply"
      />
    </div>

    <LoadingDialog v-if="loading" :text-key="loading" />
  </BigDialog>
</template>
